import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { X, ShoppingCart, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/utils/format';
import CarRentalSelector from '@/components/CarRentalSelector';
import AirportTransferSelector from '@/components/AirportTransferSelector';

function MultiRoomBooking() {
  const navigate = useNavigate();
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [cart, setCart] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [transferData, setTransferData] = useState({
    required: false,
    type: '', // 'arrival', 'departure', 'round_trip'
    arrival_flight_number: '',
    arrival_flight_time: '',
    departure_flight_number: '',
    departure_flight_time: ''
  });
  const [bookingForm, setBookingForm] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    num_guests: 1,
    special_requests: '',
    payment_option: '20_percent'  // '20_percent' or 'pay_on_arrival'
  });
  const [loading, setLoading] = useState(false);
  const [pendingBookingId, setPendingBookingId] = useState(() => {
    const stored = localStorage.getItem('pendingBookingId');
    if (stored) {
      try {
        const { id, expires } = JSON.parse(stored);
        if (Date.now() < expires) return id;
        localStorage.removeItem('pendingBookingId');
      } catch { localStorage.removeItem('pendingBookingId'); }
    }
    return null;
  });
  const isSubmittingRef = React.useRef(false);

  const savePendingBookingId = (id) => {
    if (id) {
      localStorage.setItem('pendingBookingId', JSON.stringify({
        id,
        expires: Date.now() + 30 * 60 * 1000
      }));
    } else {
      localStorage.removeItem('pendingBookingId');
    }
    setPendingBookingId(id);
  };

  const [transferPrices, setTransferPrices] = useState({ one_way: 50 });

  useEffect(() => {
    // Load cart from localStorage
    const savedCart = localStorage.getItem('bookingCart');
    const savedCheckIn = localStorage.getItem('bookingCheckIn');
    const savedCheckOut = localStorage.getItem('bookingCheckOut');
    
    if (savedCart) setCart(JSON.parse(savedCart));
    if (savedCheckIn) setCheckIn(savedCheckIn);
    if (savedCheckOut) setCheckOut(savedCheckOut);

    // Fetch transfer prices
    const fetchTransferPrices = async () => {
      try {
        const response = await axios.get(`${API}/settings/transfer-prices`);
        setTransferPrices({
          one_way: response.data.transfer_price_one_way || 50
        });
      } catch (error) {
        console.error('Failed to fetch transfer prices:', error);
      }
    };
    fetchTransferPrices();
  }, []);

  const updateQuantity = (roomTypeId, change) => {
    const updatedCart = cart.map(item => {
      if (item.room_type_id === roomTypeId) {
        const newQuantity = Math.max(0, Math.min(item.available_count, item.quantity + change));
        return { ...item, quantity: newQuantity };
      }
      return item;
    }).filter(item => item.quantity > 0);
    
    setCart(updatedCart);
    localStorage.setItem('bookingCart', JSON.stringify(updatedCart));
  };

  const removeFromCart = (roomTypeId) => {
    const updatedCart = cart.filter(item => item.room_type_id !== roomTypeId);
    setCart(updatedCart);
    localStorage.setItem('bookingCart', JSON.stringify(updatedCart));
  };

  const getTotalRooms = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  const getTotalPrice = () => {
    const roomsTotal = cart.reduce((sum, item) => sum + (item.total_price * item.quantity), 0);
    const carTotal = selectedCar ? selectedCar.car_amount : 0;
    const transferTotal = transferData.required ? transferPrices.one_way : 0;
    return roomsTotal + carTotal + transferTotal;
  };

  const getMaxGuests = () => {
    return cart.reduce((sum, item) => sum + (item.sample_room?.max_guests || 2) * item.quantity, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    // Guard against concurrent/rapid-fire submissions
    if (isSubmittingRef.current || loading) return;
    isSubmittingRef.current = true;
    setLoading(true);

    try {
      let bookingId = pendingBookingId;
      
      // Only create a new booking if we don't already have a pending one
      if (!bookingId) {
        const room_type_quantities = {};
        cart.forEach(item => {
          room_type_quantities[item.room_type_id] = item.quantity;
        });

        const bookingData = {
          room_type_quantities,
          ...bookingForm,
          check_in: checkIn,
          check_out: checkOut,
          car_id: selectedCar?.car_id || null,
          car_start_date: selectedCar?.car_start_date || null,
          car_end_date: selectedCar?.car_end_date || null,
          transfer_required: transferData.required,
          transfer_type: transferData.required ? transferData.type : null,
          arrival_flight_number: transferData.arrival_flight_number || null,
          arrival_flight_time: transferData.arrival_flight_time || null,
          departure_flight_number: transferData.departure_flight_number || null,
          departure_flight_time: transferData.departure_flight_time || null
        };

        const response = await axios.post(`${API}/bookings/by-room-type`, bookingData);
        bookingId = response.data.id;
        savePendingBookingId(bookingId);
      }
      
      // Clear cart
      localStorage.removeItem('bookingCart');
      localStorage.removeItem('bookingCheckIn');
      localStorage.removeItem('bookingCheckOut');

      // Handle payment based on payment option
      if (bookingForm.payment_option === 'pay_on_arrival') {
        savePendingBookingId(null);
        navigate(`/booking-success?booking_id=${bookingId}`);
      } else {
        const paymentResponse = await axios.post(`${API}/bookings/payment-intent`, {
          booking_id: bookingId,
          origin_url: window.location.origin,
          payment_option: bookingForm.payment_option
        });

        if (paymentResponse.data.checkout_url) {
          window.location.href = paymentResponse.data.checkout_url;
        } else {
          throw new Error('Payment gateway did not return a redirect URL. Please try again.');
        }
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to create booking');
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
          <p className="text-gray-600 mb-6">Add room types to your cart to continue</p>
          <Button onClick={() => navigate('/')}>
            Browse Room Types
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex flex-col">
      <div className="flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <Button variant="ghost" onClick={() => navigate('/')}>
              ← Back to Search
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Summary */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-6">Your Selection</h2>
                
                <div className="space-y-4">
                  {cart.map((item) => (
                    <div key={item.room_type_id} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{item.name}</h3>
                        <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span>{formatCurrency(item.price_per_night)}/night</span>
                          <span>•</span>
                          <span>{item.nights} night{item.nights !== 1 ? 's' : ''}</span>
                          <span>•</span>
                          <span>{item.available_count} available</span>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2">
                        <button
                          onClick={() => removeFromCart(item.room_type_id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="w-5 h-5" />
                        </button>
                        
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(item.room_type_id, -1)}
                            className="p-1 rounded hover:bg-gray-200"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-8 text-center font-semibold">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.room_type_id, 1)}
                            disabled={item.quantity >= item.available_count}
                            className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <div className="text-right">
                          <div className="font-bold text-blue-600">
                            {formatCurrency(item.total_price * item.quantity)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.quantity} × {formatCurrency(item.total_price)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Booking Dates */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 mb-1">Check-in</p>
                      <p className="font-semibold">{formatDate(checkIn)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Check-out</p>
                      <p className="font-semibold">{formatDate(checkOut)}</p>
                    </div>
                  </div>
                </div>

                {/* Total Summary */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Total Rooms:</span>
                    <span className="font-semibold">{getTotalRooms()}</span>
                  </div>
                  {selectedCar && (
                    <div className="flex justify-between text-sm mb-2">
                      <span>Car Rental:</span>
                      <span className="font-semibold">{formatCurrency(selectedCar.car_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t">
                    <span>Total Amount:</span>
                    <span className="text-blue-600">{formatCurrency(getTotalPrice())}</span>
                  </div>
                </div>

                {/* Car Rental Add-on */}
                <CarRentalSelector
                  checkIn={checkIn}
                  checkOut={checkOut}
                  onCarSelect={setSelectedCar}
                  selectedCar={selectedCar}
                />

                {/* Airport Transfer Add-on */}
                <AirportTransferSelector
                  transferData={transferData}
                  onTransferChange={setTransferData}
                />
              </CardContent>
            </Card>
          </div>

          {/* Booking Form */}
          <div>
            <Card className="sticky top-4">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-4">Guest Details</h3>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="guest_name">Full Name *</Label>
                    <Input
                      id="guest_name"
                      value={bookingForm.guest_name}
                      onChange={(e) => setBookingForm({ ...bookingForm, guest_name: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="guest_email">Email *</Label>
                    <Input
                      id="guest_email"
                      type="email"
                      value={bookingForm.guest_email}
                      onChange={(e) => setBookingForm({ ...bookingForm, guest_email: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="guest_phone">Phone *</Label>
                    <Input
                      id="guest_phone"
                      type="tel"
                      value={bookingForm.guest_phone}
                      onChange={(e) => setBookingForm({ ...bookingForm, guest_phone: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="num_guests">Number of Guests *</Label>
                    <Input
                      id="num_guests"
                      type="number"
                      min="1"
                      max={getMaxGuests()}
                      value={bookingForm.num_guests}
                      onChange={(e) => setBookingForm({ ...bookingForm, num_guests: parseInt(e.target.value) })}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Max {getMaxGuests()} guests
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="special_requests">Special Requests</Label>
                    <Textarea
                      id="special_requests"
                      value={bookingForm.special_requests}
                      onChange={(e) => setBookingForm({ ...bookingForm, special_requests: e.target.value })}
                      rows={3}
                    />
                  </div>

                  {/* Payment Option Section */}
                  <div className="border-t pt-4">
                    <Label className="text-base font-semibold mb-3 block">Payment Option</Label>
                    <div className="space-y-3">
                      <div 
                        className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          bookingForm.payment_option === '20_percent' 
                            ? 'border-blue-600 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setBookingForm({ ...bookingForm, payment_option: '20_percent' })}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="radio"
                            name="payment_option"
                            value="20_percent"
                            checked={bookingForm.payment_option === '20_percent'}
                            onChange={(e) => setBookingForm({ ...bookingForm, payment_option: e.target.value })}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">Pay 20% Now</div>
                            <div className="text-sm text-gray-600 mt-1">
                              Secure your reservation with a {formatCurrency(getTotalPrice() * 0.2)} deposit.
                              Remaining {formatCurrency(getTotalPrice() * 0.8)} due on arrival.
                            </div>
                          </div>
                        </div>
                      </div>

                      <div 
                        className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          bookingForm.payment_option === 'pay_on_arrival' 
                            ? 'border-blue-600 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setBookingForm({ ...bookingForm, payment_option: 'pay_on_arrival' })}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="radio"
                            name="payment_option"
                            value="pay_on_arrival"
                            checked={bookingForm.payment_option === 'pay_on_arrival'}
                            onChange={(e) => setBookingForm({ ...bookingForm, payment_option: e.target.value })}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">Pay on Arrival</div>
                            <div className="text-sm text-gray-600 mt-1">
                              Reserve now, pay {formatCurrency(getTotalPrice())} when you check in.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : `Book Now - ${formatCurrency(getTotalPrice())}`}
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    Secure payment via Stripe
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

export default MultiRoomBooking;
